import { useNavigate, type LinkProps } from '@tanstack/react-router';
import { Inbox } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import type { Conversation } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { useConversationCounts } from '../hooks/use-conversation-counts';
import { useInboxList } from '../hooks/use-inbox-list';
import { useInboxLive } from '../hooks/use-inbox-live';
import { isTypingTarget } from '../lib/keyboard';
import {
  INBOX_TABS,
  inboxListParams,
  inboxTab,
  type InboxSearch,
  type InboxTab,
} from '../schemas/inbox-search';

import { InboxFilters } from './inbox-filters';
import { InboxRow } from './inbox-row';

export interface InboxListProps {
  search: InboxSearch;
  onSearchChange: (patch: Partial<InboxSearch>) => void;
  stores: ReadonlyArray<{ id: string; name: string }>;
  pinnedStoreId?: string | undefined;
  selectedId?: string | undefined;
  threadLink: (conversation: Pick<Conversation, 'id' | 'storeId'>) => LinkProps;
}

/** Status tabs with counts, filters, rows that update live, and j/k/Enter to move and open. */
export function InboxList({
  search,
  onSearchChange,
  stores,
  pinnedStoreId,
  selectedId,
  threadLink,
}: InboxListProps) {
  const { t } = useTranslation('conversations');
  const navigate = useNavigate();
  const live = useInboxLive();
  const params = inboxListParams(search, pinnedStoreId);
  const list = useInboxList(params, live.polling);
  const counts = useConversationCounts({ refetchIntervalMs: live.polling ? 30_000 : undefined });
  const tab = inboxTab(search);
  const [cursor, setCursor] = useState(-1);
  const [now, setNow] = useState(() => Date.now());

  // Relative times and "needs reply" minutes move on without any server change.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const rows = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data]);
  const storeNames = useMemo(
    () => new Map(stores.map((store) => [store.id, store.name])),
    [stores],
  );
  const scopeIds = (pinnedStoreId ?? search.storeId)?.split(',');
  const tabCount = (status: InboxTab): number | null => {
    if (status === 'closed' || !counts.data) return null;
    return counts.data.items
      .filter((item) => !scopeIds || scopeIds.includes(item.storeId))
      .reduce((sum, item) => sum + item[status], 0);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isTypingTarget(event.target)
      )
        return;
      if (event.key === 'j' || event.key === 'k') {
        event.preventDefault();
        setCursor((current) => {
          const start = current < 0 ? rows.findIndex((row) => row.id === selectedId) : current;
          const next = event.key === 'j' ? start + 1 : start - 1;
          const clamped = Math.max(0, Math.min(rows.length - 1, next));
          document
            .querySelector(`[data-conversation-id="${rows[clamped]?.id}"]`)
            ?.scrollIntoView({ block: 'nearest' });
          return clamped;
        });
      } else if (event.key === 'Enter' && cursor >= 0) {
        const row = rows[cursor];
        if (row) {
          event.preventDefault();
          void navigate(threadLink(row));
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, cursor, selectedId, navigate, threadLink]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b px-3 pt-3 pb-2">
        <Tabs
          value={tab}
          onValueChange={(value) =>
            onSearchChange({ status: value === 'open' ? undefined : (value as InboxTab) })
          }
        >
          <TabsList className="w-full">
            {INBOX_TABS.map((status) => {
              const count = tabCount(status);
              return (
                <TabsTrigger key={status} value={status} className="flex-1 gap-1.5">
                  {t(`tabs.${status}`)}
                  {count !== null ? (
                    <span className="text-muted-foreground text-xs tabular-nums">{count}</span>
                  ) : null}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>
      <InboxFilters
        search={search}
        onSearchChange={onSearchChange}
        stores={stores}
        pinnedStoreId={pinnedStoreId}
      />
      <div className="min-h-0 flex-1 overflow-y-auto" role="list" aria-label={t('list.label')}>
        {list.isPending ? (
          <div className="space-y-3 p-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title={t(`list.empty.${tab}`)}
            description={t('list.emptyHint')}
          />
        ) : (
          <>
            {rows.map((conversation, index) => (
              <div role="listitem" key={conversation.id}>
                <InboxRow
                  conversation={conversation}
                  link={threadLink(conversation)}
                  selected={conversation.id === selectedId}
                  focused={index === cursor}
                  storeName={pinnedStoreId ? undefined : storeNames.get(conversation.storeId)}
                  now={now}
                />
              </div>
            ))}
            {list.hasNextPage ? (
              <div className="flex justify-center p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {t('list.loadMore')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
